import React, { useState } from 'react';
import { GitBranch, ArrowRight, ArrowLeft, Info } from 'lucide-react';
import Button from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { DataFlowSankey } from '../../components/design-phase';
import type { ColumnMapping, DataFlowVisualization } from '../../types/designPhase';

interface Step6Props {
  caseId: string;
  caseData: any;
  onNext: () => void;
  onBack: () => void;
}

// Coverage stats extracted from DataFlowVisualization
interface CoverageStats {
  overall_score: number;
  mapped_columns: number;
  unmapped_columns: number;
  questions_fully_covered: number;
}

export const Step6DataFlowMapping: React.FC<Step6Props> = ({
  caseId,
  caseData,
  onNext,
  onBack,
}) => {
  const [selectedMapping, setSelectedMapping] = useState<ColumnMapping | null>(null);
  const [coverageStats, setCoverageStats] = useState<CoverageStats | null>(null);

  const handleMappingSelect = (mapping: ColumnMapping) => {
    setSelectedMapping(mapping);
  };

  const handleMappingUpdate = (mapping: ColumnMapping) => {
    console.log('Mapping updated:', mapping);
    // Mappings are saved via the component's API calls
  };

  const handleDataFlowLoaded = (dataFlow: DataFlowVisualization) => {
    // Extract coverage stats from the data flow visualization
    setCoverageStats({
      overall_score: dataFlow.coverage_summary?.overall_score ?? 0,
      mapped_columns: dataFlow.mapped_columns ?? 0,
      unmapped_columns: dataFlow.unmapped_columns?.length ?? 0,
      questions_fully_covered: dataFlow.coverage_summary?.questions_fully_covered ?? 0,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-2 mb-2">
          <GitBranch className="w-6 h-6 text-purple-600" />
          <h2 className="text-2xl font-bold text-neutral-900">
            Step 6: Data Flow Mapping
          </h2>
        </div>
        <p className="text-neutral-600">
          Visualize and refine how source data flows into your Neo4j schema
        </p>
      </div>

      {/* Info Banner */}
      <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-purple-900">
            <p className="font-medium mb-1">About Data Flow Mapping</p>
            <p>
              The Sankey diagram below shows how columns from your selected enterprise systems
              map to nodes and properties in your Neo4j schema. Click on any mapping to see
              details or suggest improvements.
            </p>
          </div>
        </div>
      </div>

      {/* Coverage Summary */}
      {coverageStats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="p-4 bg-white border rounded-lg text-center">
            <p className="text-3xl font-bold text-purple-600">
              {coverageStats.overall_score.toFixed(0)}%
            </p>
            <p className="text-sm text-neutral-600">Overall Coverage</p>
          </div>
          <div className="p-4 bg-white border rounded-lg text-center">
            <p className="text-3xl font-bold text-green-600">
              {coverageStats.mapped_columns}
            </p>
            <p className="text-sm text-neutral-600">Mapped Columns</p>
          </div>
          <div className="p-4 bg-white border rounded-lg text-center">
            <p className="text-3xl font-bold text-amber-600">
              {coverageStats.unmapped_columns}
            </p>
            <p className="text-sm text-neutral-600">Unmapped Columns</p>
          </div>
          <div className="p-4 bg-white border rounded-lg text-center">
            <p className="text-3xl font-bold text-blue-600">
              {coverageStats.questions_fully_covered}
            </p>
            <p className="text-sm text-neutral-600">Questions Covered</p>
          </div>
        </div>
      )}

      {/* Sankey Visualization */}
      <div className="border rounded-lg bg-white">
        <DataFlowSankey
          caseId={caseId}
          onMappingSelect={handleMappingSelect}
          onMappingUpdate={handleMappingUpdate}
        />
      </div>

      {/* Selected Mapping Details */}
      {selectedMapping && (
        <div className="p-4 bg-white border rounded-lg">
          <h4 className="font-semibold text-neutral-900 mb-3">Selected Mapping</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Source</p>
              <p className="font-medium text-neutral-900">
                {selectedMapping.source_system_name}
              </p>
              <p className="text-sm text-neutral-600">
                {selectedMapping.source_table}.{selectedMapping.source_column}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Target</p>
              <p className="font-medium text-neutral-900">
                :{selectedMapping.target_node_label}
              </p>
              <p className="text-sm text-neutral-600">
                .{selectedMapping.target_property}
              </p>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Badge variant={selectedMapping.confidence === 'high' ? 'success' : 'warning'}>
              {selectedMapping.confidence} confidence
            </Badge>
            <Badge variant="secondary">
              {selectedMapping.status}
            </Badge>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Analysis
        </Button>
        <Button onClick={onNext}>
          Continue to Integration Pattern
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default Step6DataFlowMapping;
